/**
 * useStep6RiskAssessment — Step 6 lifecycle.
 *
 * Composes 4 structured fetches (risks, assets, threats, scenarios,
 * attack paths) + CRUD + hierarchical-approve into Step 6's body
 * props. Wire shape: `raw.result.risk_assessments`.
 *
 * Default `isChild` predicate of `useHierarchicalApprove` (`source ===
 * 'child_tara'`) is the legacy-compatible split — same as Steps 3-5.
 */
import { useCallback } from 'react';
import { getAgentResult } from '../../../api/workflow';
import { useStructuredFetch } from '../hooks/useStructuredFetch';
import { useRefetchOnReviseComplete } from '../hooks/useRefetchOnReviseComplete';
import { useRecordCRUD } from '../hooks/useRecordCRUDv2';
import { useHierarchicalApprove } from '../hooks/useHierarchicalApprove';
import type { Step6BodyProps } from '../step-components/stepBodyProps';
import type {
  PerStepState,
  WorkflowActions,
  WorkflowV2State,
} from '../state/workflowV2Types';
import type { OptionLite } from '../step-components/step6-risk/RiskFormModal';

const HAS_OUTPUT = new Set(['draft', 'revising', 'approved']);
const AGENT_NAME = 'risk_assessment_agent';
const ASSET_AGENT_NAME = 'asset_identification_agent';
const THREAT_AGENT_NAME = 'threat_analysis_agent';
const SCENARIO_AGENT_NAME = 'damage_scenario_agent';
const ATTACK_PATH_AGENT_NAME = 'attack_path_feasibility_agent';

interface AgentResultResponse {
  result?: Record<string, unknown>;
  [key: string]: unknown;
}

interface AssetLite { asset_id?: string; asset_name?: string; [key: string]: unknown; }
interface ThreatLite { threat_id?: string; threat_scenario?: string; [key: string]: unknown; }
interface ScenarioLite { damage_id?: string; damage_scenario?: string; [key: string]: unknown; }
interface AttackPathLite {
  attack_path_id?: string;
  attack_path?: string;
  asset_id?: string;
  [key: string]: unknown;
}

export interface UseStep6Input {
  threadId: string | null | undefined;
  stepState: PerStepState | undefined;
  workflowCompleted: boolean;
  analysisMode?: string | null;
  /** Reducer state — needed so `useHierarchicalApprove` can read
   *  `state.steps[6].hierarchicalModifications` (Story H2). */
  state: WorkflowV2State;
  /** Action creators bound to the reducer's dispatch. */
  actions: WorkflowActions;
}

/** Dedupe a list of records by the given primary-key field. The
 *  first label wins (typically the current-TARA row) so the dropdown
 *  stays clean when hierarchical child TARAs collide on the same id. */
function dedupeOptions<T extends Record<string, unknown>>(
  rows: T[],
  key: string,
  toLabel: (r: T) => string,
): OptionLite[] {
  const seen = new Set<string>();
  const out: OptionLite[] = [];
  for (const r of rows) {
    const id = String(r[key] ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ value: id, label: toLabel(r) });
  }
  return out;
}

export function useStep6RiskAssessment({
  threadId,
  stepState,
  workflowCompleted,
  analysisMode,
  state,
  actions,
}: UseStep6Input): Step6BodyProps {
  const status = stepState?.status ?? 'empty';
  const hasOutput = HAS_OUTPUT.has(status);
  const editable = workflowCompleted || status === 'draft';

  /* ----- Risks fetch ----- */

  const fetchRisks = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractRisks = useCallback((raw: AgentResultResponse | null): unknown[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.risk_assessments ?? r?.risk_assessment ?? r?.risks) as unknown[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const structured = useStructuredFetch<AgentResultResponse | null, unknown[]>({
    fetcher: fetchRisks,
    extract: extractRisks,
    enabled: hasOutput && !!threadId,
  });
  useRefetchOnReviseComplete(status, structured.refetch);

  /* ----- Modal option sources (assets, threats, scenarios, attack paths) -----
   * Lazy-fetched only when the form is editable. Each is deduped by
   * primary-key to handle the cross-child-TARA collisions. */

  const fetchAssets = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, ASSET_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );
  const extractAssets = useCallback((raw: AgentResultResponse | null): AssetLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.assets ?? r?.identified_assets) as AssetLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);
  const assetsFetch = useStructuredFetch<AgentResultResponse | null, AssetLite[]>({
    fetcher: fetchAssets,
    extract: extractAssets,
    enabled: editable && !!threadId,
  });
  const assetOptions: OptionLite[] = dedupeOptions(
    (assetsFetch.data ?? []) as AssetLite[],
    'asset_id',
    (a) => (a.asset_name ? `${a.asset_id} — ${a.asset_name}` : String(a.asset_id ?? '')),
  );

  const fetchThreats = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, THREAT_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );
  const extractThreats = useCallback((raw: AgentResultResponse | null): ThreatLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.threats_identified ?? r?.threats) as ThreatLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);
  const threatsFetch = useStructuredFetch<AgentResultResponse | null, ThreatLite[]>({
    fetcher: fetchThreats,
    extract: extractThreats,
    enabled: editable && !!threadId,
  });
  const threatOptions: OptionLite[] = dedupeOptions(
    (threatsFetch.data ?? []) as ThreatLite[],
    'threat_id',
    (t) => {
      const desc = t.threat_scenario
        ? String(t.threat_scenario).slice(0, 50) + (String(t.threat_scenario).length > 50 ? '…' : '')
        : '';
      return desc ? `${t.threat_id} — ${desc}` : String(t.threat_id ?? '');
    },
  );

  const fetchScenarios = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, SCENARIO_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );
  const extractScenarios = useCallback((raw: AgentResultResponse | null): ScenarioLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.damage_scenarios ?? r?.scenarios) as ScenarioLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);
  // Always-on once Step 6 has output (not just when editable): read mode
  // derives each risk's Impact column from its damage scenario's
  // final_impact_rating via the `impactByDamageId` map below (AUMOVIO
  // TC-052 — the agent's own impact_rating field is unreliable/stale).
  const scenariosFetch = useStructuredFetch<AgentResultResponse | null, ScenarioLite[]>({
    fetcher: fetchScenarios,
    extract: extractScenarios,
    enabled: hasOutput && !!threadId,
  });
  const scenarioOptions: OptionLite[] = dedupeOptions(
    (scenariosFetch.data ?? []) as ScenarioLite[],
    'damage_id',
    (s) => {
      const desc = s.damage_scenario
        ? String(s.damage_scenario).slice(0, 50) + (String(s.damage_scenario).length > 50 ? '…' : '')
        : '';
      return desc ? `${s.damage_id} — ${desc}` : String(s.damage_id ?? '');
    },
  );

  const fetchAttackPaths = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, ATTACK_PATH_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );
  const extractAttackPaths = useCallback((raw: AgentResultResponse | null): AttackPathLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.attack_analysis ?? r?.attack_paths ?? r?.attack_path_analyses) as
      | AttackPathLite[]
      | undefined;
    return Array.isArray(list) ? list : [];
  }, []);
  // Always-on once Step 6 has output: needed for both the modal's
  // attack_path_id select (editable) AND the read-mode asset lookup
  // (newer risk rows emit `attack_path_id` but no direct `asset_id`,
  // so we resolve asset_id by chasing the attack path's own asset_id
  // field via the `apIdToAssetId` map below).
  const attackPathsFetch = useStructuredFetch<AgentResultResponse | null, AttackPathLite[]>({
    fetcher: fetchAttackPaths,
    extract: extractAttackPaths,
    enabled: hasOutput && !!threadId,
  });
  const attackPathOptions: OptionLite[] = dedupeOptions(
    (attackPathsFetch.data ?? []) as AttackPathLite[],
    'attack_path_id',
    (p) => {
      const desc = p.attack_path
        ? String(p.attack_path).slice(0, 50) + (String(p.attack_path).length > 50 ? '…' : '')
        : '';
      return desc ? `${p.attack_path_id} — ${desc}` : String(p.attack_path_id ?? '');
    },
  );

  // attack_path_id → asset_id lookup. Lets the dispatcher backfill
  // `asset_id` on risk rows that lack it (newer agent records do
  // this — older ones embed asset_id directly). First-write wins so
  // a current-TARA path shadows duplicates from child TARAs.
  const apIdToAssetId: Record<string, string> = {};
  for (const p of (attackPathsFetch.data ?? []) as AttackPathLite[]) {
    if (typeof p.attack_path_id === 'string' && typeof p.asset_id === 'string') {
      if (!apIdToAssetId[p.attack_path_id]) {
        apIdToAssetId[p.attack_path_id] = p.asset_id;
      }
    }
  }

  // AUMOVIO TC-052 — the two drivers of every risk value, derived from
  // their source records rather than the risk rows' own (often blank or
  // stale) copies. Mirrors the backend recalculation engine's joins:
  //   impact:      damage_id → damage scenario final_impact_rating
  //   feasibility: threat_id → strongest attack_feasibility_rating
  //                across that threat's attack paths (never understate)
  const impactByDamageId: Record<string, string> = {};
  for (const s of (scenariosFetch.data ?? []) as ScenarioLite[]) {
    const id = s.damage_id;
    const rating = (s as Record<string, unknown>).final_impact_rating;
    if (typeof id === 'string' && typeof rating === 'string' && rating && !impactByDamageId[id]) {
      impactByDamageId[id] = rating;
    }
  }

  const FEASIBILITY_RANK: Record<string, number> = { 'very low': 0, low: 1, medium: 2, high: 3 };
  const feasibilityByThreatId: Record<string, string> = {};
  for (const p of (attackPathsFetch.data ?? []) as AttackPathLite[]) {
    const tid = (p as Record<string, unknown>).threat_id;
    const rating = (p as Record<string, unknown>).attack_feasibility_rating;
    if (typeof tid !== 'string' || typeof rating !== 'string' || !rating) continue;
    const current = feasibilityByThreatId[tid];
    if (
      current === undefined ||
      (FEASIBILITY_RANK[rating.toLowerCase()] ?? -1) > (FEASIBILITY_RANK[current.toLowerCase()] ?? -1)
    ) {
      feasibilityByThreatId[tid] = rating;
    }
  }

  /* ----- CRUD ----- */

  const crud = useRecordCRUD({
    threadId,
    agentName: AGENT_NAME,
    enabled: editable,
  });

  /* ----- Hierarchical approve ----- */

  const hierarchicalEnabled = analysisMode === 'hierarchical';
  const records = (structured.data ?? []) as Array<Record<string, unknown>>;
  const hierApprove = useHierarchicalApprove({
    stepId: 6,
    threadId,
    agentName: AGENT_NAME,
    enabled: hierarchicalEnabled,
    records,
    primaryKey: 'risk_id',
    state,
    actions,
    onApproved: structured.refetch,
  });

  return {
    status,
    errorMessage: stepState?.errorMessage ?? null,
    editable,
    structured,
    crud,
    assetOptions,
    threatOptions,
    scenarioOptions,
    attackPathOptions,
    apIdToAssetId,
    impactByDamageId,
    feasibilityByThreatId,
    hierarchical: {
      enabled: hierarchicalEnabled,
      modifications: hierApprove.modifications,
      setRelevance: hierApprove.setRelevance,
      setJustification: hierApprove.setJustification,
      onApprove: hierApprove.onApprove,
      approving: hierApprove.approving,
      approveError: hierApprove.approveError,
    },
    approveOverride: hierarchicalEnabled
      ? { onApprove: hierApprove.onApprove, approving: hierApprove.approving }
      : undefined,
  };
}
